import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { of } from 'rxjs';
import { catchError, map, switchMap, take, timeout } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';

/**
 * Factory function to create permission-based route guards
 * @param permissionCheck Function that checks if user has the required permission
 * @param redirectPath Path to redirect to if permission is denied
 * @param permissionName Name of the permission (for logging)
 */
export function createPermissionGuard(
  permissionCheck: (authService: AuthService) => boolean,
  redirectPath: string = '/dashboard',
  permissionName: string = 'required permission',
): CanActivateFn {
  return (route, state) => {
    const authService = inject(AuthService);
    const router = inject(Router);

    // Check if user is authenticated
    if (!authService.isLoggedIn()) {
      // Store the attempted URL for redirecting after login
      sessionStorage.setItem('redirectUrl', state.url);
      router.navigate(['/login']);
      return false;
    }

    // Get current roles, and if empty, trigger user data loading
    const currentRoles = authService.getCurrentUserRoles();

    if (currentRoles.length === 0) {
      // Trigger user data loading and wait for roles
      return authService.getCurrentUser().pipe(
        // Switch to the roles observable after loading user data
        switchMap(() => authService.userRoles$),
        // Wait up to 10 seconds for roles to load
        timeout(10000),
        // Take the first emission with roles
        take(1),
        // Check permission
        map(() =>
          checkPermission(
            authService,
            router,
            permissionCheck,
            redirectPath,
            permissionName,
            state,
          ),
        ),
        // Handle errors
        catchError((error) => {
          console.error(
            `Error loading user roles in ${permissionName} guard:`,
            error,
          );
          // Store the attempted URL before redirecting
          sessionStorage.setItem('redirectUrl', state.url);
          router.navigate([redirectPath]);
          return of(false);
        }),
      );
    }

    // Roles already loaded, check immediately
    return checkPermission(
      authService,
      router,
      permissionCheck,
      redirectPath,
      permissionName,
      state,
    );
  };
}

function checkPermission(
  authService: AuthService,
  router: Router,
  permissionCheck: (authService: AuthService) => boolean,
  redirectPath: string,
  permissionName: string,
  state?: any,
): boolean {
  const hasAccess = permissionCheck(authService);

  if (!hasAccess) {
    console.warn(`Access denied: User does not have ${permissionName}`);
    // Store the attempted URL before redirecting
    if (state?.url) {
      sessionStorage.setItem('redirectUrl', state.url);
      // If user is logged in but lacks permission, go to dashboard
      // If not logged in at all, this will be handled by auth guard
      router.navigate([redirectPath]);
    } else {
      router.navigate([redirectPath]);
    }
    return false;
  }

  return true;
}

/**
 * Guard for routes requiring CanViewClientMatters permission
 */
export const clientMattersGuard: CanActivateFn = createPermissionGuard(
  (authService) => authService.canViewClientMatters(),
  '/dashboard',
  'CanViewClientMatters',
);

/**
 * Guard for routes requiring CanViewUsersManagement permission
 */
export const usersManagementGuard: CanActivateFn = createPermissionGuard(
  (authService) => authService.canViewUsersManagement(),
  '/dashboard',
  'CanViewUsersManagement',
);

/**
 * Guard for routes requiring CanViewPracticeAreasManagement permission
 */
export const practiceAreasManagementGuard: CanActivateFn =
  createPermissionGuard(
    (authService) => authService.canViewPracticeAreasManagement(),
    '/dashboard',
    'CanViewPracticeAreasManagement',
  );

/**
 * Guard for routes requiring CanViewFeatureFlagManagement permission
 * (kept separate for clarity, but could use the factory)
 */
export const featureFlagManagementGuard: CanActivateFn = createPermissionGuard(
  (authService) => authService.canViewFeatureFlagManagement(),
  '/dashboard',
  'CanViewFeatureFlagManagement',
);

/**
 * Guard for routes requiring CanViewAppSettingsManagement permission
 */
export const appSettingsManagementGuard: CanActivateFn = createPermissionGuard(
  (authService) => authService.canViewAppSettingsManagement(),
  '/dashboard',
  'CanViewAppSettingsManagement',
);

/**
 * Guard for routes requiring CanViewRequests permission
 */
export const requestsGuard: CanActivateFn = createPermissionGuard(
  (authService) => authService.canViewRequests(),
  '/dashboard',
  'CanViewRequests',
);

/**
 * Guard for routes requiring CanViewPermissionsManagement permission
 */
export const permissionsManagementGuard: CanActivateFn = createPermissionGuard(
  (authService) => authService.canViewPermissionsManagement(),
  '/dashboard',
  'CanViewPermissionsManagement',
);

/**
 * Guard for routes requiring CanViewLogs permission
 */
export const logsGuard: CanActivateFn = createPermissionGuard(
  (authService) => authService.canViewLogs(),
  '/dashboard',
  'CanViewLogs',
);

/**
 * Guard for routes requiring CanViewAuditHistory permission
 */
export const auditHistoryGuard: CanActivateFn = createPermissionGuard(
  (authService) => authService.canViewAuditHistory(),
  '/dashboard',
  'CanViewAuditHistory',
);

/**
 * Guard for routes requiring CanViewUserFeedback permission
 */
export const userFeedbackGuard: CanActivateFn = createPermissionGuard(
  (authService) => authService.canViewUserFeedback(),
  '/dashboard',
  'CanViewUserFeedback',
);
